import { useState } from 'react';

import { ShoppingCartOutlined } from '@mui/icons-material';
import { Badge, Button, Divider, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { Bbar } from 'components/docked';
import { ListPagination } from 'components/pagination';
import { CurrencyProgressText, CurrencyText, LabeledText, WeightProgressText } from 'components/text';
import { useListagemContext } from 'context/ListagemContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { PedidoCartBottomSheet } from 'pages/compra/pedido/components';
import { useFrete, usePedidoMinimo, usePostPedido, useQueryProdutos } from 'pages/compra/pedido/hooks';

export function PedidoBbar() {
    const { data } = useQueryProdutos();
    const { produtoCartList, configPedido } = usePedidoCompraContext();
    const { page, setPage } = useListagemContext();
    const { handleSubmit, isLoading } = usePostPedido();
    const { isEmptyCart, carrinhoChanged } = usePedidoCompraContext();
    const { isUpMd, isUpLg } = useBreakpoints();
    const { vlPedido } = usePedidoCompraContext();
    const { vlFrete, hasFreteSection, hasVlFrete, isFreteGratis, vlBaseFreteGratis, progressFrete } = useFrete();
    const { hasMinimoSection, tpControlePedidoMinimoLoja, vlPesoLiquido, vlPedidoMinimoLoja, progressMinimo } = usePedidoMinimo();
    const isPeso = tpControlePedidoMinimoLoja === 'PESO_TOTAL';
    const [openCartModal, setOpenCartModal] = useState(false);

    return (
        <>
            <PedidoCartBottomSheet open={openCartModal} setOpen={setOpenCartModal} />

            <Bbar sx={{ gap: { xs: 1.5, lg: 1.5 } }}>
                {/* Total de produtos + paginação */}
                {data && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <Typography sx={{ 'fontSize': 13, 'color': 'text.secondary', '&>strong': { color: 'text.primary' }, 'minWidth': 174 }}>
                                <strong>{data.data.length}</strong> de <strong>{data.total}</strong> produtos listados
                            </Typography>

                            <Box sx={{ display: 'flex', flex: 1, justifyContent: { xs: 'flex-end', md: 'center' } }}>
                                {data.total > 200 && <ListPagination data={data} list={data?.data} limit={200} page={page} setPage={setPage} />}
                            </Box>
                        </Box>

                        {!isUpMd && <Divider orientation={'horizontal'} flexItem sx={{ borderStyle: 'dashed' }} />}
                    </>
                )}

                {/* Peso/valor mínimo + frete grátis + frete*/}
                {(hasFreteSection || hasVlFrete || hasMinimoSection) && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            {hasMinimoSection &&
                                (isPeso ? (
                                    <WeightProgressText
                                        label="Peso Mínimo"
                                        value={vlPesoLiquido}
                                        target={vlPedidoMinimoLoja}
                                        progress={progressMinimo!}
                                    />
                                ) : (
                                    <CurrencyProgressText
                                        label="Valor Mínimo"
                                        value={vlPedido}
                                        target={vlPedidoMinimoLoja}
                                        progress={progressMinimo!}
                                    />
                                ))}

                            {hasFreteSection && (
                                <CurrencyProgressText label="Frete Grátis" value={vlPedido} target={vlBaseFreteGratis} progress={progressFrete!} />
                            )}

                            {hasVlFrete && (
                                <>
                                    {isFreteGratis && (
                                        <LabeledText
                                            label="Frete"
                                            color="success.main"
                                            value={
                                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'success.main', lineHeight: 0.85 }}>
                                                    GRÁTIS
                                                </Typography>
                                            }
                                        />
                                    )}

                                    {!isFreteGratis && <CurrencyText label="Frete" value={vlFrete} />}
                                </>
                            )}
                        </Box>

                        <Divider orientation={isUpMd ? 'vertical' : 'horizontal'} flexItem sx={{ borderStyle: { xs: 'dashed', md: 'solid' } }} />
                    </>
                )}

                {/* Total do pedido + total do pedido no mobile*/}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', md: 'auto' } }}>
                    {!isUpMd && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: 'primary.main',
                            }}
                        >
                            TOTAL DO PEDIDO
                        </Typography>
                    )}

                    <CurrencyText label={isUpMd ? 'Total do pedido' : undefined} size={isUpMd ? 'md' : 'lg'} value={vlPedido + vlFrete} />
                </Box>

                {/* Botão de salvar + acessar carrinho no mobile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                    {!isUpLg && (
                        <Badge color="primary" badgeContent={produtoCartList.length}>
                            <Button variant="outlined" onClick={() => setOpenCartModal(true)} sx={{ width: { xs: 124, md: 'auto' } }}>
                                <ShoppingCartOutlined />
                            </Button>
                        </Badge>
                    )}

                    <Tooltip title="Continuar para o carrinho">
                        <Box sx={{ width: { xs: '100%', md: 150 } }}>
                            <Button
                                variant="contained"
                                loading={isLoading}
                                onClick={handleSubmit}
                                disabled={!configPedido || (isEmptyCart && !carrinhoChanged)}
                                sx={{ width: '100%' }}
                            >
                                Salvar Pedido
                            </Button>
                        </Box>
                    </Tooltip>
                </Box>
            </Bbar>
        </>
    );
}
